import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentUser, fetchUserAttributes } from "aws-amplify/auth";
import { client } from "../../amplify-client";
import { markWalkthroughCompleted } from "../../api/walkthrough";
import {
  evaluateClientTextModeration,
  extractModerationFeedback,
} from "../../api/moderation";
import {
  unhideConversationForUser,
  deleteChatLifecycle,
  isPendingRequestExpired,
} from "../../api/communication";
import { getEventApiHttpUrl } from "../../api/config";

export function normalizeSearchFilters(filters = {}) {
  return Object.fromEntries(
    Object.entries(filters)
      .map(([key, value]) => {
        if (key === "minAge" || key === "maxAge") {
          if (value === "" || value == null) return [key, undefined];
          const numericValue = Number(value);
          return [key, Number.isFinite(numericValue) ? numericValue : undefined];
        }

        if (typeof value === "string") {
          const trimmed = value.trim();
          if (trimmed === "" || trimmed === "ANY") {
            return [key, undefined];
          }
          return [key, trimmed];
        }

        return [key, value];
      })
      .filter(([key, value]) => {
        if (key === "onlineStatus") return value !== "all";
        if (Array.isArray(value)) return value.length > 0;
        return value !== undefined && value !== null;
      })
  );
}

export function getActiveSociopoliticalRanges(sociopoliticalFilters = {}) {
  return Object.fromEntries(
    Object.entries(sociopoliticalFilters)
      .filter(([, data]) => !data?.irrelevant)
      .map(([key, data]) => [key, { min: data?.min, max: data?.max }])
  );
}

export function buildSearchPayload(filters = {}, sociopoliticalFilters = {}, options = {}) {
  const { defaultDiscovery = false } = options;
  const cleanFilters = normalizeSearchFilters(filters);
  const activeSociopolitical = getActiveSociopoliticalRanges(sociopoliticalFilters);

  const payload = {
    ...cleanFilters,
    ...(Object.keys(activeSociopolitical).length > 0
      ? { sociopolitical: activeSociopolitical }
      : {}),
  };

  if (defaultDiscovery) {
    payload.defaultDiscovery = true;
  }

  return payload;
}

export function filterSearchResults(users, { currentUserId = "", blockedUserIds = [] } = {}) {
  const blockedSet = new Set((blockedUserIds || []).map((value) => String(value || "").trim()));
  const currentUser = String(currentUserId || "").trim();

  return (Array.isArray(users) ? users : []).filter((user) => {
    const userId = String(user?.cognitoId || "").trim();
    if (!userId) return false;
    if (currentUser && userId === currentUser) return false;
    if (blockedSet.has(userId)) return false;
    return true;
  });
}

const REASON_FOR_CHAT_MAX_LENGTH = 200;
const DEFAULT_DISCOVERY_RESULT_CAP = 8;
const DEFAULT_DISCOVERY_CACHE_TTL_MS = 60 * 1000;
const CARD_DESCRIPTION_MAX_CHARS = 96;

export function getCommunityCardDescriptionMaxChars(viewportWidth) {
  const width = Number(viewportWidth);
  if (!Number.isFinite(width) || width <= 0) return CARD_DESCRIPTION_MAX_CHARS;
  if (width <= 360) return 42;
  if (width <= 420) return 48;
  if (width <= 640) return 58;
  return CARD_DESCRIPTION_MAX_CHARS;
}

let defaultDiscoveryCache = {
  users: [],
  expiresAtMs: 0,
};
let defaultDiscoveryRequestPromise = null;
const stalePendingCleanupAttemptByChatId = new Set();

function pickDefaultDiscoveryUsers(users) {
  const normalized = Array.isArray(users) ? [...users] : [];
  if (normalized.length <= DEFAULT_DISCOVERY_RESULT_CAP) {
    return normalized;
  }

  for (let index = normalized.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    const current = normalized[index];
    normalized[index] = normalized[swapIndex];
    normalized[swapIndex] = current;
  }

  return normalized.slice(0, DEFAULT_DISCOVERY_RESULT_CAP);
}

function hasFreshDefaultDiscoveryCache() {
  return (
    Array.isArray(defaultDiscoveryCache.users) &&
    defaultDiscoveryCache.expiresAtMs > Date.now()
  );
}

function buildPairChatPk(userA, userB) {
  const [first, second] = [String(userA || ""), String(userB || "")].sort();
  return `CHAT#PAIR#${first}#${second}`;
}

async function cleanupStalePendingChat(chatPk) {
  const normalizedChatPk = String(chatPk || "").trim();
  if (!normalizedChatPk || stalePendingCleanupAttemptByChatId.has(normalizedChatPk)) return;
  stalePendingCleanupAttemptByChatId.add(normalizedChatPk);
  try {
    await deleteChatLifecycle(normalizedChatPk);
  } catch (error) {
    console.warn("Unable to delete stale pending chat:", { chatPk: normalizedChatPk, error });
  }
}

// Member-gated single-chat fetch. Direct Chat.list/get from clients is denied
// at the model level — only chat_members can read a Chat row, enforced by the
// chatRead lambda behind getMyChat.
async function fetchChatByPk(chatPk) {
  const resp = await client.queries.getMyChat({ chatPk });
  const errors = Array.isArray(resp?.errors) ? resp.errors : [];
  if (errors.length) {
    throw new Error(errors.map((err) => err.message).join(", "));
  }
  return resp?.data ?? null;
}

function isRequestTimeoutError(error) {
  const messages = [
    error?.message,
    ...(Array.isArray(error?.errors) ? error.errors.map((entry) => entry?.message) : []),
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" | ");
  return /Task timed out after/i.test(messages);
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function recoverTimedOutChatRequest({ senderId, recipientId, maxAttempts = 4 }) {
  const chatPk = buildPairChatPk(senderId, recipientId);
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const chat = await fetchChatByPk(chatPk);
      const members = Array.isArray(chat?.chat_members) ? chat.chat_members : [];
      const status = String(chat?.status || "").trim().toLowerCase();
      const isExpiredPending =
        status === "pending" &&
        isPendingRequestExpired({
          status,
          request_created_at: chat?.createdAt || chat?.updatedAt || "",
        });
      if (isExpiredPending) {
        void cleanupStalePendingChat(chatPk);
        return null;
      }
      if (
        chat &&
        !chat?.isGroup &&
        members.includes(senderId) &&
        members.includes(recipientId) &&
        (status === "pending" || status === "accepted")
      ) {
        return chatPk;
      }
    } catch (error) {
      console.warn("Timed-out request recovery check failed:", error);
    }
    if (attempt < maxAttempts - 1) {
      await sleep(350);
    }
  }
  return null;
}

// Picks the most user-friendly display name for a Community card or profile action.
export function pickCommunityName(user) {
  return user?.profileName || user?.displayName || user?.name || user?.email || "User";
}

// Picks the descriptive profile text shown in Community card previews.
export function pickCommunityBio(user) {
  return user?.story || user?.bio || user?.about || "";
}

// Extracts the canonical Cognito id used for Community messaging and moderation actions.
export function pickCommunityRecipientId(user) {
  return user?.cognitoId || "";
}

// Truncates long Community card descriptions into a stable preview length.
export function toCommunityCardDescriptionPreview(
  text,
  maxChars = CARD_DESCRIPTION_MAX_CHARS
) {
  const normalized = String(text || "").trim();
  if (!normalized) return "";
  if (normalized.length <= maxChars) return normalized;
  return `${normalized.slice(0, Math.max(0, maxChars - 3)).trimEnd()}...`;
}

// Validates Community request-composer text before sending a conversation request.
export function validateCommunityDiscussionTopic(topicValue) {
  const normalized = String(topicValue || "").trim();
  if (!normalized) return "A request message is required.";
  if (normalized.length > REASON_FOR_CHAT_MAX_LENGTH) {
    return `Request message must be ${REASON_FOR_CHAT_MAX_LENGTH} characters or fewer.`;
  }
  const localModeration = evaluateClientTextModeration({
    fieldType: "topic",
    text: normalized,
  });
  if (localModeration?.blocked) {
    return localModeration.feedback;
  }
  return "";
}

// Loads and coordinates Community discovery results, filters, pair-chat state, and request actions.
export function useCommunityData(options = {}) {
  const { isGuest = false } = options;
  const navigate = useNavigate();
  const initialSociopoliticalFilters = {
    immigration: { min: 1, max: 7, irrelevant: true },
    gunControl: { min: 1, max: 7, irrelevant: true },
    abortion: { min: 1, max: 7, irrelevant: true },
    climateChange: { min: 1, max: 7, irrelevant: true },
    taxPolicy: { min: 1, max: 7, irrelevant: true },
    lawEnforcement: { min: 1, max: 7, irrelevant: true },
    votingIntegrity: { min: 1, max: 7, irrelevant: true },
    regulatoryPhilosophy: { min: 1, max: 7, irrelevant: true },
    tradePolicy: { min: 1, max: 7, irrelevant: true },
    healthcare: { min: 1, max: 7, irrelevant: true },
  };

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [results, setResults] = useState([]);
  // Cursor returned by the last searchUsers call. Empty string means
  // either we've reached the end or this was a default-discovery
  // (random sample) response that doesn't paginate.
  const [searchNextToken, setSearchNextToken] = useState("");
  // The exact filter payload used for the most recent runSearch, kept so
  // loadMoreSearchResults can request the next page with identical
  // filters. Cursor pagination only works when the query stays the same
  // between page calls.
  const lastSearchPayloadRef = useRef(null);
  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [currentUserId, setCurrentUserId] = useState("");
  const [filters, setFilters] = useState({
    username: "",
    state: "",
    city: "",
    politicalAffiliation: "",
    religiousAffiliation: "",
    minAge: "",
    maxAge: "",
    gender: "",
    ethnicity: "",
    interests: [],
    onlineStatus: "all",
  });
  const [sociopoliticalFilters, setSociopoliticalFilters] = useState(initialSociopoliticalFilters);

  const sociopoliticalQuestions = useMemo(
    () => [
      { id: "immigration", label: "Immigration", leftLabel: "More Open Immigration", rightLabel: "Stricter Border Enforcement" },
      { id: "gunControl", label: "Gun Control", leftLabel: "Stricter Gun Laws", rightLabel: "Expanded Gun Rights" },
      { id: "abortion", label: "Abortion", leftLabel: "Broader Access", rightLabel: "More Restrictions" },
      { id: "climateChange", label: "Climate Change", leftLabel: "Urgent Climate Action", rightLabel: "Lower Climate Priority" },
      { id: "taxPolicy", label: "Tax Policy", leftLabel: "Higher Taxes & Services", rightLabel: "Lower Taxes & Smaller Government" },
      { id: "lawEnforcement", label: "Law Enforcement", leftLabel: "Reallocate Police Funding", rightLabel: "Expand Police Support" },
      { id: "votingIntegrity", label: "Voting Integrity", leftLabel: "Easier Ballot Access", rightLabel: "Stronger Voter ID Rules" },
      { id: "regulatoryPhilosophy", label: "Regulatory Philosophy", leftLabel: "More Federal Regulation", rightLabel: "Less Federal Regulation" },
      { id: "tradePolicy", label: "Trade Policy", leftLabel: "Free Trade Focus", rightLabel: "Tariff Protection Focus" },
      { id: "healthcare", label: "Healthcare", leftLabel: "Public Coverage Focus", rightLabel: "Private Coverage Focus" },
    ],
    []
  );

  const interestOptions = useMemo(
    () => [
      "Travel", "Cooking", "Sports", "Reading", "Music",
      "Art", "Technology", "Gaming", "Fitness", "Photography",
      "Writing", "Movies", "Fashion", "Volunteering", "Nature",
    ],
    []
  );

  const refreshBlockedUsers = useCallback(async () => {
    if (isGuest) {
      setBlockedUserIds([]);
      return [];
    }

    const query = client?.queries?.listBlockedUsers;
    if (typeof query !== "function") {
      return [];
    }

    const response = await query({ limit: 200 });
    if (response?.errors?.length) {
      throw new Error(response.errors.map((e) => e.message).join(", "));
    }

    const ids = [...new Set(
      (Array.isArray(response?.data) ? response.data : [])
        .map((row) => String(row?.cognitoId || "").trim())
        .filter(Boolean)
    )];

    setBlockedUserIds(ids);
    return ids;
  }, [isGuest]);

  useEffect(() => {
    if (isGuest) {
      setCurrentUserId("");
      setBlockedUserIds([]);
      return () => {};
    }

    let cancelled = false;

    async function loadCurrentUserAndBlocks() {
      try {
        const user = await getCurrentUser();
        if (!cancelled) {
          setCurrentUserId(String(user?.userId || ""));
        }
      } catch (error) {
        console.error("Failed to load current user id for discovery filters:", error);
      }

      try {
        await refreshBlockedUsers();
      } catch (error) {
        console.error("Failed to load blocked user list:", error);
      }
    }

    loadCurrentUserAndBlocks();
    return () => {
      cancelled = true;
    };
  }, [isGuest, refreshBlockedUsers]);

  function handleFilterChange(event) {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }

  function handleInterestToggle(interest) {
    setFilters((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((entry) => entry !== interest)
        : [...prev.interests, interest],
    }));
  }

  function handleSociopoliticalChange(questionId, type, value) {
    setSociopoliticalFilters((prev) => {
      const newValue = parseInt(value, 10);
      const current = prev[questionId];

      if (type === "min") {
        return {
          ...prev,
          [questionId]: {
            ...current,
            min: Math.min(newValue, current.max),
          },
        };
      }

      return {
        ...prev,
        [questionId]: {
          ...current,
          max: Math.max(newValue, current.min),
        },
      };
    });
  }

  function handleIrrelevantToggle(questionId) {
    setSociopoliticalFilters((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        irrelevant: !prev[questionId].irrelevant,
      },
    }));
  }

  function setAllSociopoliticalRelevance(isRelevant) {
    setSociopoliticalFilters((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([key, data]) => [
          key,
          { ...data, irrelevant: !isRelevant },
        ])
      )
    );
  }

  async function runSearch(options = {}) {
    const { defaultDiscovery = false } = options;
    let shouldUseDefaultDiscovery = defaultDiscovery;
    setLoading(true);
    try {
      const searchPayload = buildSearchPayload(filters, sociopoliticalFilters, {
        defaultDiscovery,
      });
      shouldUseDefaultDiscovery =
        defaultDiscovery || Object.keys(searchPayload).length === 0;
      if (shouldUseDefaultDiscovery && !searchPayload.defaultDiscovery) {
        searchPayload.defaultDiscovery = true;
      }

      const serializedFilters = JSON.stringify(searchPayload);

      const performSearchRequest = async ({ nextToken } = {}) => {
        const response = await client.queries.searchUsers(
          {
            filters: serializedFilters,
            ...(nextToken ? { nextToken } : {}),
          },
          isGuest ? { authMode: "identityPool" } : undefined
        );

        if (response?.errors?.length) {
          throw new Error(response.errors.map((e) => e.message).join(", "));
        }

        // New shape: { items, nextToken }. Defensively support old
        // array-shape responses during rollout.
        const data = response?.data;
        if (Array.isArray(data)) return { items: data, nextToken: null };
        return {
          items: Array.isArray(data?.items) ? data.items : [],
          nextToken: String(data?.nextToken || "") || null,
        };
      };

      let pageItems = [];
      let pageNextToken = null;
      if (shouldUseDefaultDiscovery && hasFreshDefaultDiscoveryCache()) {
        pageItems = defaultDiscoveryCache.users;
      } else if (shouldUseDefaultDiscovery && defaultDiscoveryRequestPromise) {
        const cached = await defaultDiscoveryRequestPromise;
        pageItems = cached?.items || [];
      } else if (shouldUseDefaultDiscovery) {
        defaultDiscoveryRequestPromise = performSearchRequest();
        try {
          const cached = await defaultDiscoveryRequestPromise;
          pageItems = cached?.items || [];
          defaultDiscoveryCache = {
            users: pageItems,
            expiresAtMs: Date.now() + DEFAULT_DISCOVERY_CACHE_TTL_MS,
          };
        } finally {
          defaultDiscoveryRequestPromise = null;
        }
      } else {
        const page = await performSearchRequest();
        pageItems = page.items;
        pageNextToken = page.nextToken;
      }

      const filteredData = filterSearchResults(pageItems, {
        currentUserId,
        blockedUserIds,
      });
      const nextResults = shouldUseDefaultDiscovery
        ? pickDefaultDiscoveryUsers(filteredData)
        : filteredData;
      setResults(nextResults);
      // Default discovery is a random sample — no nextToken (each
      // request would shuffle differently anyway). Filter searches save
      // both the cursor and the payload so loadMoreSearchResults can
      // continue with identical filters.
      if (shouldUseDefaultDiscovery) {
        setSearchNextToken("");
        lastSearchPayloadRef.current = null;
      } else {
        setSearchNextToken(pageNextToken || "");
        lastSearchPayloadRef.current = serializedFilters;
      }
    } catch (error) {
      console.error("Community search failed:", error);
      const detail =
        error?.message ||
        error?.errors?.map?.((entry) => entry?.message).filter(Boolean).join(", ") ||
        "Unknown error";

      const isTemporarySearchRestriction =
        shouldUseDefaultDiscovery &&
        /temporary search restriction|repetitive search activity|rate.?limit|too many search attempts/i.test(
          detail
        );

      if (
        isTemporarySearchRestriction &&
        Array.isArray(defaultDiscoveryCache.users) &&
        defaultDiscoveryCache.expiresAtMs > 0
      ) {
        const fallbackData = filterSearchResults(defaultDiscoveryCache.users, {
          currentUserId,
          blockedUserIds,
        });
        setResults(pickDefaultDiscoveryUsers(fallbackData));
        return;
      }
      if (isTemporarySearchRestriction) {
        return;
      }

      setResults([]);

      if (isGuest && /unauth|authoriz|no valid auth tokens|not authorized/i.test(detail)) {
        alert("Please sign in to browse community members.");
        navigate("/active");
        return;
      }

      alert(`Failed to search users: ${detail}`);
    } finally {
      setLoading(false);
    }
  }

  function resetFilters() {
    setFilters({
      username: "",
      state: "",
      city: "",
      politicalAffiliation: "",
      religiousAffiliation: "",
      minAge: "",
      maxAge: "",
      gender: "",
      ethnicity: "",
      interests: [],
      onlineStatus: "all",
    });

    setSociopoliticalFilters(initialSociopoliticalFilters);
    setResults([]);
    setSearchNextToken("");
    lastSearchPayloadRef.current = null;
  }

  // Fetch the next cursor-paginated page of results for the current
  // search and append them to the existing list. No-op if there are no
  // more results, no active payload (last call was default discovery),
  // or a load is already in flight.
  async function loadMoreSearchResults() {
    if (loadingMore) return;
    if (!searchNextToken) return;
    const payload = lastSearchPayloadRef.current;
    if (!payload) return;

    setLoadingMore(true);
    try {
      const response = await client.queries.searchUsers(
        { filters: payload, nextToken: searchNextToken },
        isGuest ? { authMode: "identityPool" } : undefined
      );
      if (response?.errors?.length) {
        throw new Error(response.errors.map((e) => e.message).join(", "));
      }
      const data = response?.data;
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : [];
      const cursor = Array.isArray(data) ? null : String(data?.nextToken || "") || null;

      const filtered = filterSearchResults(items, {
        currentUserId,
        blockedUserIds,
      });
      // Dedupe by cognitoId in case the cursor cross-paged a profile we
      // already have (filters that reorder underneath us, etc.).
      setResults((prev) => {
        const seen = new Set(prev.map((u) => String(u?.cognitoId || "")));
        const additions = filtered.filter(
          (u) => !seen.has(String(u?.cognitoId || ""))
        );
        return prev.concat(additions);
      });
      setSearchNextToken(cursor || "");
    } catch (error) {
      console.error("Community load-more failed:", error);
    } finally {
      setLoadingMore(false);
    }
  }

  async function initiateMessageRequest(recipientUser, discussionTopicInput, options = {}) {
    const {
      navigateOnSuccess = true,
      suppressErrorAlert = false,
    } = options;
    let senderId = "";
    let recipientId = "";
    let recipientName = "";
    let discussionTopic = "Direct message";

    try {
      const currentUser = await getCurrentUser();
      const attrs = await fetchUserAttributes();
      senderId = currentUser?.userId;
      const senderDisplayName =
        attrs?.name || attrs?.preferred_username || attrs?.email || "User";
      discussionTopic = String(discussionTopicInput ?? "Direct message").trim() || "Direct message";

      if (!senderId) {
        const error = new Error("Unable to send conversation request. Please log in again.");
        if (!suppressErrorAlert) alert(error.message);
        return null;
      }
      if (discussionTopic.length > REASON_FOR_CHAT_MAX_LENGTH) {
        const error = new Error(
          `Request message must be ${REASON_FOR_CHAT_MAX_LENGTH} characters or fewer.`
        );
        if (!suppressErrorAlert) alert(error.message);
        return null;
      }

      recipientId = recipientUser?.cognitoId;
      if (!recipientId) {
        console.error("No canonical recipient Cognito ID found:", recipientUser);
        const error = new Error(
          "Unable to send conversation request. Recipient profile is missing Cognito ID."
        );
        if (!suppressErrorAlert) alert(error.message);
        return null;
      }
      if (recipientId === senderId) {
        const error = new Error("You cannot message yourself.");
        if (!suppressErrorAlert) alert(error.message);
        return null;
      }
      if (blockedUserIds.includes(recipientId)) {
        const error = new Error("Unable to send a request to this user.");
        if (!suppressErrorAlert) alert(error.message);
        return null;
      }

      recipientName =
        recipientUser?.profileName || recipientUser?.displayName || recipientUser?.name || "User";
      const mutation = client?.mutations?.initiateChatRequest;
      if (typeof mutation !== "function") {
        throw new Error("initiateChatRequest mutation is unavailable. Deploy backend schema updates.");
      }

      const eventApiHttpUrl = getEventApiHttpUrl();
      const requestResp = await mutation({
        recipientId,
        recipientName,
        senderName: senderDisplayName,
        discussionTopic,
        ...(eventApiHttpUrl ? { eventApiHttpUrl } : {}),
      });

      if (requestResp?.errors?.length) {
        throw new Error(requestResp.errors.map((err) => err.message).join(", "));
      }

      const result = requestResp?.data || {};
      if (result?.success === false) {
        throw new Error(result?.message || "Failed to initiate chat request.");
      }

      const chatPk = result?.chatId;
      if (!chatPk) {
        throw new Error("Chat request response did not include a chat id.");
      }

      try {
        await unhideConversationForUser({ userId: senderId, chatPk });
      } catch (error) {
        console.warn("Unable to unhide conversation after request send:", error);
      }

      try {
        const user = await getCurrentUser();
        if (user?.userId) {
          await markWalkthroughCompleted({ client, userId: user.userId });
        }
      } catch (error) {
        console.error("Failed to update walkthrough completion:", error);
      }
      try {
        window.dispatchEvent(
          new CustomEvent("walkthrough:action", { detail: { action: "request-sent" } })
        );
      } catch (error) {
        // Ignore if CustomEvent is unavailable.
      }

      if (navigateOnSuccess) {
        navigate("/active", {
          state: { selectedChatPk: chatPk },
        });
      }

      return {
        chatPk,
        recipientId,
        recipientName,
      };
    } catch (error) {
      console.error("Failed to initiate message request:", error);
      if (isRequestTimeoutError(error) && senderId && recipientId) {
        const recoveredChatPk = await recoverTimedOutChatRequest({
          senderId,
          recipientId,
        });
        if (recoveredChatPk) {
          try {
            await unhideConversationForUser({ userId: senderId, chatPk: recoveredChatPk });
          } catch (unhideError) {
            console.warn("Unable to unhide recovered conversation:", unhideError);
          }
          if (navigateOnSuccess) {
            navigate("/active", {
              state: { selectedChatPk: recoveredChatPk },
            });
          }
          return {
            chatPk: recoveredChatPk,
            recipientId,
            recipientName: recipientName || "User",
            recoveredAfterTimeout: true,
          };
        }
      }
      const moderationFeedback = extractModerationFeedback(error);
      if (moderationFeedback.isModerationError) {
        if (!suppressErrorAlert) {
          alert(moderationFeedback.feedback);
        }
        // Surface the friendly per-category feedback even when alerts are
        // suppressed (e.g. the request composer renders inline error text).
        // Returning a tagged shape lets the caller distinguish a content
        // block from a generic failure and show the right message.
        return { blocked: true, reason: "moderation", feedback: moderationFeedback.feedback };
      }
      const rawMessage = String(error?.message || "");
      const isBlockedByCounterpart = /USER_BLOCKED/i.test(rawMessage);
      const fallbackMessage = isBlockedByCounterpart
        ? "This person isn't available."
        : rawMessage || "Failed to send conversation request. Please try again.";
      if (!suppressErrorAlert) {
        alert(fallbackMessage);
      }
      return {
        blocked: true,
        reason: isBlockedByCounterpart ? "blocked" : "error",
        feedback: fallbackMessage,
      };
    }
  }

  async function blockUser(blockedUserId) {
    const userId = String(blockedUserId || "").trim();
    if (!userId) return false;
    if (currentUserId && userId === currentUserId) {
      throw new Error("Cannot block yourself.");
    }

    const mutation = client?.mutations?.blockUser;
    if (typeof mutation !== "function") {
      throw new Error("blockUser mutation is unavailable. Deploy backend schema updates.");
    }

    const response = await mutation({ blockedUserId: userId });
    if (response?.errors?.length) {
      throw new Error(response.errors.map((err) => err.message).join(", "));
    }

    setBlockedUserIds((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
    setResults((prev) => prev.filter((user) => String(user?.cognitoId || "") !== userId));
    return true;
  }

  async function unblockUser(blockedUserId) {
    const userId = String(blockedUserId || "").trim();
    if (!userId) return false;

    const mutation = client?.mutations?.unblockUser;
    if (typeof mutation !== "function") {
      throw new Error("unblockUser mutation is unavailable. Deploy backend schema updates.");
    }

    const response = await mutation({ blockedUserId: userId });
    if (response?.errors?.length) {
      throw new Error(response.errors.map((err) => err.message).join(", "));
    }

    setBlockedUserIds((prev) => prev.filter((id) => id !== userId));
    return true;
  }

  function calculateAge(dateOfBirth) {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1;
    }
    return age;
  }

  const resolvePairChatState = useCallback(
    async (recipientIdInput) => {
      if (isGuest) return null;

      const senderId = String(currentUserId || "").trim();
      const recipientId = String(recipientIdInput || "").trim();
      if (!senderId || !recipientId || senderId === recipientId) {
        return null;
      }

      const chatPk = buildPairChatPk(senderId, recipientId);
      const chat = await fetchChatByPk(chatPk);
      const members = Array.isArray(chat?.chat_members) ? chat.chat_members : [];
      const status = String(chat?.status || "").trim().toLowerCase();
      const isExpiredPending =
        status === "pending" &&
        isPendingRequestExpired({
          status,
          request_created_at: chat?.createdAt || chat?.updatedAt || "",
        });
      const isAllowedStatus = status === "accepted" || status === "pending";
      if (
        !chat ||
        chat?.isGroup ||
        isExpiredPending ||
        !isAllowedStatus ||
        !members.includes(senderId) ||
        !members.includes(recipientId)
      ) {
        if (isExpiredPending) {
          void cleanupStalePendingChat(chatPk);
        }
        return null;
      }

      return {
        chatPk,
        status,
      };
    },
    [currentUserId, isGuest]
  );

  return {
    loading,
    loadingMore,
    hasMoreSearchResults: Boolean(searchNextToken),
    loadMoreSearchResults,
    results,
    blockedUserIds,
    currentUserId,
    filters,
    setFilters,
    sociopoliticalFilters,
    sociopoliticalQuestions,
    interestOptions,
    handleFilterChange,
    handleInterestToggle,
    handleSociopoliticalChange,
    handleIrrelevantToggle,
    setAllSociopoliticalRelevance,
    runSearch,
    resetFilters,
    blockUser,
    unblockUser,
    refreshBlockedUsers,
    initiateMessageRequest,
    resolvePairChatState,
    calculateAge,
  };
}
